import React from 'react';
import { useExecutivePerformanceTable } from '../../hooks/useExecutiveQueries';
import { LoadingSpinner, HorizontalTableShell } from '../ui';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { formatCurrency } from '../../utils/formatters';

interface PerformanceTableProps {
  branchId?: number;
  currency?: string;
  timeRange?: any;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

interface TableRowData {
  yearMonth: string;
  month: string;
  revenue: number;
  invoices: number;
  generatedCredits: number;
  usedCredits: number;
  creditUsageRate: number;
  creditToRevenue: number;
  creditAmount?: number;
}

const PerformanceTable: React.FC<PerformanceTableProps> = ({
  branchId,
  currency,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  // Hooks
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  console.log(currency)
  // Component metadata
  const componentId = 'executive-performance-table';
  const componentTitle = 'جدول تحلیلی عملکرد دوره زمانی';
  
  // Fetch performance table data with timeRange
  const { data, isLoading, error } = useExecutivePerformanceTable(branchId, timeRange?.timeRange);
  
  const tableData = data?.monthlyData || [];
  
  // Calculate totals on frontend
  const totals = {
    revenue: tableData.reduce((sum: number, item: TableRowData) => sum + (item.revenue || 0), 0),
    invoices: tableData.reduce((sum: number, item: TableRowData) => sum + (item.invoices || 0), 0),
    generatedCredits: tableData.reduce((sum: number, item: TableRowData) => sum + (item.generatedCredits || 0), 0),
    usedCredits: tableData.reduce((sum: number, item: TableRowData) => sum + (item.usedCredits || 0), 0),
    creditUsageRate: 0,
    creditToRevenue: 0
  };
  
  // Calculate average rates
  totals.creditUsageRate = totals.generatedCredits > 0 
    ? (totals.usedCredits / totals.generatedCredits * 100) 
    : 0;
  
  // Need to calculate total credit amount for creditToRevenue
  const totalCreditAmount = tableData.reduce((sum: number, item: TableRowData) => sum + (item.creditAmount || 0), 0);
  totals.creditToRevenue = totals.revenue > 0 
    ? (totalCreditAmount / totals.revenue * 100) 
    : 0;
  
  // Handle loading state
  if (showLoading && isLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[300px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری جدول عملکرد…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error
  if (error) {
    if (onError && error instanceof Error) {
      onError(error);
    }
    return null;
  }
  
  return (
    <div className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="table"
        dashboardSource="executive"
        onInfo={() => openGuide(CHART_GUIDES.executivePerformanceTable)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'executive',
          componentType: 'table',
          description: 'مقایسه عملکرد شاخص‌های کلیدی در دوره مورد نظر',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 gap-6'
          }
        })}
        isFavorited={isFavorited(componentId)}
        className="mb-4"
      />
      <div className="mb-6">
        <h3 className="title-card">{componentTitle}</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">مقایسه عملکرد شاخص‌های کلیدی در دوره مورد نظر</p>
      </div>
      
      {tableData.length > 0 ? (
        <HorizontalTableShell minWidth={900}>
          <table className={`w-full border-collapse ${compact ? 'text-sm' : ''}`} aria-label="جدول تحلیلی عملکرد دوره زمانی">
            <thead className="bg-bg-secondary border-b-2" style={{borderColor: 'var(--border-secondary)'}}>
              <tr>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">ماه</th>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">فروش</th>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">سفارش</th>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">اعتبار تولیدی</th>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">اعتبار مصرفی</th>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">نرخ مصرف</th>
                <th className="px-6 py-3 text-center text-sm font-bold text-text-primary" scope="col">سهم از فروش</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((item: TableRowData, index: number) => (
                <tr 
                  key={index} 
                  className={`border-b transition-colors ${index % 2 === 0 ? 'bg-bg-card' : 'bg-bg-secondary'} hover:bg-bg-tertiary`}
                  style={{borderColor: 'var(--border-primary)'}}
                >
                  <td className="table-cell font-medium">
                    {item.month}
                  </td>
                  <td className="table-cell">
                    {formatCurrency(parseInt((item.revenue).toFixed(0)),currency)}
                    
                  </td>
                  <td className="table-cell">
                    {item.invoices.toLocaleString()}
                  </td>
                  <td className="table-cell">
                    {item.generatedCredits.toLocaleString()}
                  </td>
                  <td className="table-cell">
                    {item.usedCredits.toLocaleString()}
                  </td>
                  <td className="table-cell">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.creditUsageRate > 11 ? 'bg-status-success-bg text-status-success border border-status-success' : 
                      item.creditUsageRate > 9 ? 'bg-status-warning-bg text-status-warning border border-status-warning' : 'bg-status-error-bg text-status-error border border-status-error'
                    }`}>
                      {item.creditUsageRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.creditToRevenue > 0.7 ? 'bg-status-success-bg text-status-success border border-status-success' : 
                      item.creditToRevenue > 0.5 ? 'bg-status-warning-bg text-status-warning border border-status-warning' : 'bg-status-error-bg text-status-error border border-status-error'
                    }`}>
                      {item.creditToRevenue.toFixed(2)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-primary-50 border-t-2 border-primary-200 font-bold">
                <td className="table-cell text-primary-900 py-4">مجموع/میانگین</td>
                <td className="table-cell text-primary-900 py-4 text-lg">{formatCurrency(parseInt((totals.revenue).toFixed(0)),currency)}</td>
                <td className="table-cell text-primary-900 py-4 text-lg">{totals.invoices.toLocaleString()}</td>
                <td className="table-cell text-primary-900 py-4 text-lg">{totals.generatedCredits.toLocaleString()}</td>
                <td className="table-cell text-primary-900 py-4 text-lg">{totals.usedCredits.toLocaleString()}</td>
                <td className="table-cell py-4">
                  <span className="px-3 py-2 rounded-md text-sm font-bold bg-primary-600 text-white shadow-sm">{totals.creditUsageRate.toFixed(1)}%</span>
                </td>
                <td className="table-cell py-4">
                  <span className="px-3 py-2 rounded-md text-sm font-bold bg-primary-600 text-white shadow-sm">{totals.creditToRevenue.toFixed(2)}%</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </HorizontalTableShell>
      ) : (
          <div className="p-8 text-center content-tertiary">
            <p>داده‌ای برای نمایش جدول وجود ندارد</p>
          </div>
        )}
    </div>
  );
};

export default PerformanceTable;